import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { FileText, FlaskConical, Loader2, UploadCloud } from 'lucide-react';
import { labApi } from '@/lib/api';
import { Select } from '@/components/ui/Select';
import { LabConfiguratorShell } from './LabConfiguratorShell';

interface LabBrandOption {
  id: string;
  name: string;
}

interface ImportProposal {
  id: string;
  fieldName: string;
  fieldType: 'user_input' | 'field' | 'choice';
  title: string;
  value: string;
  sourceExcerpt: string;
  confidence: number;
  required: boolean;
  options: Array<{ value: string; label: string }>;
}

interface LabCreateWorkflowModalProps {
  product: { id: string; name: string; brandId?: string | null };
  onClose: () => void;
  onStarted: (mode: 'scratch' | 'import') => void;
  embedded?: boolean;
  showBrandSelect?: boolean;
  onBusyChange?: (busy: boolean) => void;
}

type SourceMode = 'scratch' | 'file';
type Stage = 'setup' | 'analyzing' | 'review' | 'applying';

function sourcePreview(value: string, maxChars = 700): string {
  return value.length > maxChars ? `${value.slice(0, maxChars).trimEnd()}…` : value;
}

function importError(error: unknown, fallback: string): string {
  if (error && typeof error === 'object' && 'response' in error) {
    const response = (error as { response?: { data?: { error?: string } } }).response;
    return response?.data?.error || fallback;
  }
  return error instanceof Error ? error.message : fallback;
}

function WorkflowFrame({
  embedded,
  title,
  description,
  icon,
  onClose,
  closeDisabled,
  size,
  footer,
  children,
}: {
  embedded: boolean;
  title: string;
  description: string;
  icon: ReactNode;
  onClose: () => void;
  closeDisabled?: boolean;
  size?: 'wide';
  footer?: ReactNode;
  children: ReactNode;
}) {
  if (!embedded) {
    return (
      <LabConfiguratorShell
        title={title}
        description={description}
        icon={icon}
        onClose={onClose}
        closeDisabled={closeDisabled}
        size={size}
        footer={footer}
      >
        {children}
      </LabConfiguratorShell>
    );
  }

  return (
    <section className="overflow-hidden rounded-xl border border-accent-primary/25 bg-accent-primary/[0.04]">
      <div className="flex items-start gap-3 border-b border-border-subtle px-4 py-4">
        <span className="mt-0.5 text-accent-primary">{icon}</span>
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-text-primary">{title}</h3>
          <p className="mt-1 text-xs leading-relaxed text-text-muted">{description}</p>
        </div>
      </div>
      <div className="space-y-5 px-4 py-5">{children}</div>
      {footer && <div className="border-t border-border-subtle px-4 py-4">{footer}</div>}
    </section>
  );
}

export function LabCreateWorkflowModal({
  product,
  onClose,
  onStarted,
  embedded = false,
  showBrandSelect = true,
  onBusyChange,
}: LabCreateWorkflowModalProps) {
  const [brands, setBrands] = useState<LabBrandOption[]>([]);
  const [brandId, setBrandId] = useState(product.brandId || '');
  const [sourceMode, setSourceMode] = useState<SourceMode>('scratch');
  const [file, setFile] = useState<File | null>(null);
  const [stage, setStage] = useState<Stage>('setup');
  const [progress, setProgress] = useState(0);
  const [importId, setImportId] = useState<string | null>(null);
  const [proposals, setProposals] = useState<ImportProposal[]>([]);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    if (showBrandSelect) {
      labApi.getBrands()
        .then(response => setBrands(response.data.data?.brands || []))
        .catch(() => setError('Nie udało się pobrać marek.'));
    }
    return () => { mountedRef.current = false; };
  }, [showBrandSelect]);

  useEffect(() => {
    if (!importId || stage !== 'analyzing') return;
    const timer = window.setInterval(async () => {
      try {
        const response = await labApi.getPipelineImport(importId);
        if (!mountedRef.current) return;
        const data = response.data.data;
        setProgress(data.progress || 0);
        if (data.status === 'READY') {
          setProposals(data.proposals || []);
          setStage('review');
        } else if (data.status === 'FAILED' || data.status === 'STALE') {
          setError(data.error || 'Analiza dokumentu nie powiodła się.');
          setStage('setup');
          setImportId(null);
        }
      } catch (pollError) {
        setError(importError(pollError, 'Nie udało się odczytać postępu analizy.'));
      }
    }, 1500);
    return () => window.clearInterval(timer);
  }, [importId, stage]);

  useEffect(() => {
    const busy = stage === 'analyzing' || stage === 'applying';
    onBusyChange?.(busy);
    return () => onBusyChange?.(false);
  }, [onBusyChange, stage]);

  const missingRequired = useMemo(
    () => proposals.filter(proposal => proposal.required && !proposal.value.trim()),
    [proposals],
  );
  const recognizedCount = useMemo(
    () => proposals.filter(proposal => proposal.sourceExcerpt.trim()).length,
    [proposals],
  );

  const acceptFile = (nextFile?: File | null) => {
    setError(null);
    if (!nextFile) return;
    const extension = nextFile.name.toLowerCase().split('.').pop();
    if (!['txt', 'md'].includes(extension || '')) {
      setFile(null);
      setError('Wybierz plik z rozszerzeniem .txt lub .md.');
      return;
    }
    if (nextFile.size > 2 * 1024 * 1024 || nextFile.size === 0) {
      setFile(null);
      setError('Plik jest pusty albo przekracza limit 2 MiB.');
      return;
    }
    setFile(nextFile);
  };

  const handleStart = async () => {
    setError(null);
    try {
      await labApi.updateProduct(product.id, { brandId: brandId || null });
      if (sourceMode === 'scratch') {
        onStarted('scratch');
        return;
      }
      if (!file) {
        setError('Dodaj jeden plik TXT lub MD.');
        return;
      }
      setStage('analyzing');
      setProgress(1);
      const response = await labApi.startPipelineImport(product.id, 'KNOWLEDGE', file);
      setImportId(response.data.data.id);
    } catch (startError) {
      setStage('setup');
      setError(importError(startError, 'Nie udało się rozpocząć importu.'));
    }
  };

  const handleApply = async () => {
    if (!importId || missingRequired.length > 0) return;
    setStage('applying');
    setError(null);
    try {
      await labApi.applyPipelineImport(
        importId,
        proposals.map(proposal => ({ id: proposal.id, value: proposal.value })),
      );
      onStarted('import');
    } catch (applyError) {
      const message = importError(applyError, 'Nie udało się zatwierdzić importu.');
      const stale = applyError && typeof applyError === 'object' && 'response' in applyError
        && (applyError as { response?: { status?: number } }).response?.status === 409
        && message.includes('Konfiguracja pipeline');
      setStage(stale ? 'setup' : 'review');
      if (stale) setImportId(null);
      setError(message);
    }
  };

  const updateProposal = (id: string, value: string) => {
    setProposals(current => current.map(proposal => proposal.id === id ? { ...proposal, value } : proposal));
  };

  if (stage === 'analyzing' || stage === 'applying') {
    return (
      <WorkflowFrame
        embedded={embedded}
        title={stage === 'analyzing' ? 'Analizuję dokument' : 'Zapisuję import'}
        description={stage === 'analyzing' ? 'AI dopasowuje treści wyłącznie do pól aktualnego pipeline’u.' : 'Tworzę checkpointy i uruchamiam brakujące kroki.'}
        icon={<Loader2 className="h-6 w-6 animate-spin" />}
        onClose={onClose}
        closeDisabled
      >
        <div className="space-y-3 py-8">
          <div className="flex justify-between text-sm text-text-secondary">
            <span>{stage === 'analyzing' ? 'Postęp analizy' : 'Zatwierdzanie'}</span>
            <span>{stage === 'analyzing' ? progress : 100}%</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-surface-hover">
            <div className="h-full rounded-full bg-accent-primary transition-all" style={{ width: `${stage === 'analyzing' ? progress : 100}%` }} />
          </div>
        </div>
      </WorkflowFrame>
    );
  }

  if (stage === 'review') {
    return (
      <WorkflowFrame
        embedded={embedded}
        title="Sprawdź rozpoznane dane"
        description="Treść z dokumentu została zachowana bez przepisywania. Każdą wartość możesz poprawić przed zapisaniem."
        icon={<FileText className="h-6 w-6" />}
        onClose={onClose}
        size="wide"
        footer={(
          <div className="flex items-center justify-between gap-4">
            <button type="button" onClick={handleApply} disabled={missingRequired.length > 0} className="btn-primary disabled:opacity-50">
              Zatwierdź i uruchom brakujące kroki
            </button>
            <button type="button" onClick={onClose} className="btn-secondary">Anuluj</button>
          </div>
        )}
      >
        {error && <p role="alert" className="rounded-lg border border-danger/25 bg-danger/10 p-3 text-sm text-danger">{error}</p>}
        {missingRequired.length > 0 && (
          <p className="rounded-lg border border-warning/25 bg-warning/10 p-3 text-sm text-warning">
            Uzupełnij wymagane pola: {missingRequired.map(item => item.title).join(', ')}.
          </p>
        )}
        <div className={`rounded-lg border p-3 text-sm ${recognizedCount > 0 ? 'border-accent-primary/25 bg-accent-primary/10 text-text-secondary' : 'border-danger/25 bg-danger/10 text-danger'}`}>
          Rozpoznano dane dla <strong>{recognizedCount} z {proposals.length}</strong> pól pipeline
          {proposals.length > 0 ? ` (${Math.round((recognizedCount / proposals.length) * 100)}%)` : ''}.
          {recognizedCount > 0
            ? ' Puste sekcje zostaną przygotowane przez standardowy pipeline po zatwierdzeniu.'
            : ' Dokument nie został poprawnie dopasowany. Nie zatwierdzaj importu i spróbuj ponownie.'}
        </div>
        <div className="grid gap-4 lg:grid-cols-2">
          {proposals.map(proposal => (
            <section key={proposal.id} className="rounded-xl border border-border bg-surface-hover/30 p-4">
              <div className="mb-3 flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-sm font-semibold text-text-primary">{proposal.title}</h3>
                  <p className="text-xs text-text-muted">{proposal.fieldType} · {Math.round(proposal.confidence * 100)}% pewności</p>
                </div>
                {proposal.required && <span className="rounded-full bg-danger/10 px-2 py-1 text-[10px] font-medium text-danger">Wymagane</span>}
              </div>
              {proposal.options.length > 0 ? (
                <Select value={proposal.value} onChange={event => updateProposal(proposal.id, event.target.value)}>
                  <option value="">Wybierz wartość</option>
                  {proposal.options.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                </Select>
              ) : (
                <textarea
                  value={proposal.value}
                  onChange={event => updateProposal(proposal.id, event.target.value)}
                  rows={5}
                  className="w-full resize-y rounded-lg border border-border bg-surface-raised p-3 text-sm text-text-primary focus:border-accent-primary focus:outline-none"
                />
              )}
              {proposal.sourceExcerpt && (
                <blockquote className="mt-3 whitespace-pre-wrap break-words border-l-2 border-accent-primary/40 pl-3 text-xs leading-relaxed text-text-muted">
                  Źródło: {sourcePreview(proposal.sourceExcerpt)}
                </blockquote>
              )}
            </section>
          ))}
        </div>
      </WorkflowFrame>
    );
  }

  return (
    <WorkflowFrame
      embedded={embedded}
      title="Utwórz Laboratorium produktu"
      description={`${showBrandSelect ? 'Wybierz markę i sposób' : 'Wybierz sposób'} przygotowania bazy wiedzy dla produktu „${product.name}”.`}
      icon={<FlaskConical className="h-6 w-6" />}
      onClose={onClose}
      footer={(
        <div className="flex items-center justify-between gap-4">
          <button type="button" onClick={handleStart} className="btn-primary">Dalej</button>
          <button type="button" onClick={onClose} className="btn-secondary">Anuluj</button>
        </div>
      )}
    >
      {error && <p role="alert" className="rounded-lg border border-danger/25 bg-danger/10 p-3 text-sm text-danger">{error}</p>}
      {showBrandSelect && (
        <div>
          <label className="mb-2 block text-sm font-medium text-text-primary">Marka</label>
          <Select value={brandId} onChange={event => setBrandId(event.target.value)}>
            <option value="">Bez marki</option>
            {brands.map(brand => <option key={brand.id} value={brand.id}>{brand.name}</option>)}
          </Select>
        </div>
      )}
      <div className="grid gap-3 md:grid-cols-2">
        <button
          type="button"
          onClick={() => setSourceMode('scratch')}
          className={`rounded-xl border p-4 text-left transition-colors ${sourceMode === 'scratch' ? 'border-accent-primary bg-accent-primary/10' : 'border-border hover:bg-surface-hover'}`}
        >
          <span className="block text-sm font-semibold text-text-primary">Tworzę od zera</span>
          <span className="mt-1 block text-xs text-text-muted">Uruchom standardowy instalator bazy wiedzy.</span>
        </button>
        <button
          type="button"
          onClick={() => setSourceMode('file')}
          className={`rounded-xl border p-4 text-left transition-colors ${sourceMode === 'file' ? 'border-accent-primary bg-accent-primary/10' : 'border-border hover:bg-surface-hover'}`}
        >
          <span className="block text-sm font-semibold text-text-primary">Mam gotową bazę wiedzy</span>
          <span className="mt-1 block text-xs text-text-muted">Zaimportuj jeden dokument TXT lub MD.</span>
        </button>
      </div>
      {sourceMode === 'file' && (
        <label
          onDragOver={event => event.preventDefault()}
          onDrop={event => { event.preventDefault(); acceptFile(event.dataTransfer.files[0]); }}
          className="flex cursor-pointer flex-col items-center justify-center rounded-xl border border-dashed border-accent-primary/40 bg-accent-primary/5 px-6 py-10 text-center hover:bg-accent-primary/10"
        >
          <UploadCloud className="mb-3 h-8 w-8 text-accent-primary" />
          <span className="text-sm font-medium text-text-primary">{file ? file.name : 'Upuść plik tutaj lub kliknij, aby wybrać'}</span>
          <span className="mt-1 text-xs text-text-muted">TXT lub MD, maksymalnie 2 MiB</span>
          <input type="file" accept=".txt,.md,text/plain,text/markdown" className="sr-only" onChange={event => acceptFile(event.target.files?.[0])} />
        </label>
      )}
    </WorkflowFrame>
  );
}
